import { siteText, speakerCredit } from '../siteText'
import { SourceMoments, useMomentMark } from '../components/SourceMoments'
import { useViewActive } from '../viewActivity'
import { SourcePipeline } from '../components/SourcePipeline'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TranscriptLine } from '../components/TranscriptLine'
import { seekAndPlay } from '../transcriptInteraction'
import { isTranscriptScrollKey, scrollTranscriptToActive } from '../transcriptFollow'
import { ArrowDown, ArrowRight, AudioLines, ChevronsUpDown, Film, Pause, Play, Plus, RefreshCw, Square, Volume2, VolumeX } from 'lucide-react'
import { api, ApiRequestError } from '../api'
import { WorkspaceAction, WorkspaceActions } from '../components/WorkspaceHeader'
import { DeleteButton, UndoToast } from '../components/Deletion'
import { useAsync } from '../hooks'
import type { LiveMonitor, LiveTranscriptEvent, LiveTranscriptItem, LiveTranscriptStatus, Source, TranscriptSegment } from '../types'
import { Button, WorkspaceLoading, formatDuration } from '../components/ui'
import { LiveSourcePlayer, type PlayerState } from '../components/LiveSourcePlayer'
import { LiveWorkspaceLoading, type LiveWorkspaceLoadingProgress } from '../components/LiveWorkspaceLoading'
import { isLiveWorkspaceReady, type MediaLoadProgress } from '../liveLoadingProgress'
import { Dropdown } from '../components/Dropdown'
import { HomeStart } from '../components/HomeStart'
import { SourceComposer, type SourceComposerIntent } from './SourcesView'
import { retryLatestCapture } from '../liveRetry'
import { sourceTimelineAvailable } from '../sourcePlayback'
import { canReconnectDesktopCapture, isDesktopCaptureActive, isDesktopCaptureDisconnected } from '../components/CaptureStatus'
import './HomeView.css'

const activeStates = new Set(['queued', 'ingesting', 'transcribing', 'live'])

type LiveConnectionState = 'idle' | LiveTranscriptStatus

// The SSE endpoint may rotate a connection on a keep-alive boundary. Give
// the reconnect loop a moment to recover before putting a warning in the
// transcript header; a normal ~60 second rotation should be invisible.
const LIVE_TRANSCRIPT_RECONNECT_NOTICE_MS = 2500

interface DisplayTranscriptSegment extends TranscriptSegment {
  id?: string
  live?: boolean
  final?: boolean
}

function normalizeTranscriptText(text: string) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

function parseLiveTranscriptEvent(raw: string): LiveTranscriptEvent | null {
  let parsed: unknown
  try { parsed = JSON.parse(raw) } catch { return null }
  if (!parsed || typeof parsed !== 'object') return null
  const record = parsed as Record<string, unknown>
  const status = record.status
  if (status !== 'connecting' && status !== 'live' && status !== 'error' && status !== 'stopped') return null
  const items = Array.isArray(record.items) ? record.items.flatMap((value): LiveTranscriptItem[] => {
    if (!value || typeof value !== 'object') return []
    const item = value as Record<string, unknown>
    const start = typeof item.start === 'number' ? item.start : Number(item.start)
    const end = typeof item.end === 'number' ? item.end : Number(item.end)
    if (item.text == null || !Number.isFinite(start) || !Number.isFinite(end)) return []
    return [{
      id: item.id == null ? `${start}:${end}:${String(item.text)}` : String(item.id),
      text: String(item.text),
      start: Math.max(0, start),
      end: Math.max(start, end),
      final: item.final === true,
      speaker: typeof item.speaker === 'string' ? item.speaker : undefined,
      speaker_name: typeof item.speaker_name === 'string' ? item.speaker_name : undefined,
    }]
  }) : []
  return { status, items, error: typeof record.error === 'string' ? record.error : undefined }
}

function liveItemsMatch(left: LiveTranscriptItem, right: LiveTranscriptItem) {
  if (left.id === right.id) return true
  if (Math.abs(left.start - right.start) > .9) return false
  const leftText = normalizeTranscriptText(left.text)
  const rightText = normalizeTranscriptText(right.text)
  if (!leftText || !rightText) return false
  return leftText === rightText || (Math.min(leftText.length, rightText.length) >= 12 && (leftText.includes(rightText) || rightText.includes(leftText)))
}

function upsertLiveTranscript(existing: LiveTranscriptItem[], incoming: LiveTranscriptItem[]) {
  const next = [...existing]
  for (const item of incoming) {
    if (!item.text.trim()) continue
    const index = next.findIndex((candidate) => liveItemsMatch(candidate, item))
    if (index === -1) next.push(item)
    else next[index] = item
  }
  return next.sort((left, right) => left.start - right.start || left.end - right.end)
}

export function useLiveTranscript(sourceId: string, enabled: boolean) {
  const viewActive = useViewActive()
  const [items, setItems] = useState<LiveTranscriptItem[]>([])
  const [status, setStatus] = useState<LiveConnectionState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)

  useEffect(() => {
    setItems([])
    setStatus('idle')
    setError(null)
    setConnected(false)
    setReconnecting(false)
  }, [sourceId])

  useEffect(() => {
    if (!enabled || !viewActive) {
      if (!enabled) setItems([])
      setConnected(false)
      setReconnecting(false)
      setStatus((current) => current === 'idle' ? current : 'stopped')
      return
    }

    let mounted = true
    let reconnectTimer: number | null = null
    let reconnectNoticeTimer: number | null = null
    let reconnectAttempt = 0
    let stream: EventSource | null = null

    const closeStream = () => {
      const current = stream
      stream = null
      current?.close()
    }

    const scheduleReconnect = () => {
      if (!mounted || reconnectTimer !== null) return
      // EventSource retries ordinary network failures, but it permanently
      // closes on an HTTP error such as an expired session. Own the retry so
      // both cases recover after the first failed reconnect.
      const delay = Math.min(10000, 750 * (2 ** Math.min(reconnectAttempt, 3)))
      reconnectAttempt += 1
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null
        if (mounted) openStream()
      }, delay)
    }

    const scheduleReconnectNotice = () => {
      if (!mounted || reconnectNoticeTimer !== null) return
      reconnectNoticeTimer = window.setTimeout(() => {
        reconnectNoticeTimer = null
        if (mounted) setReconnecting(true)
      }, LIVE_TRANSCRIPT_RECONNECT_NOTICE_MS)
    }

    const clearReconnectNotice = () => {
      if (reconnectNoticeTimer !== null) {
        window.clearTimeout(reconnectNoticeTimer)
        reconnectNoticeTimer = null
      }
      setReconnecting(false)
    }

    function openStream() {
      if (!mounted) return
      closeStream()
      const next = new EventSource(`/api/sources/${encodeURIComponent(sourceId)}/transcript/live`, { withCredentials: true })
      stream = next
      const handleMessage = (event: Event) => {
        if (!mounted || stream !== next) return
        const message = event as MessageEvent<string>
        const payload = parseLiveTranscriptEvent(message.data)
        if (!payload) return
        setStatus(payload.status)
        if (payload.error) setError(payload.error)
        else if (payload.status === 'live' || payload.status === 'connecting') setError(null)
        if (payload.items.length) setItems((current) => upsertLiveTranscript(current, payload.items))
      }
      next.onopen = () => {
        if (!mounted || stream !== next) return
        reconnectAttempt = 0
        if (reconnectTimer !== null) { window.clearTimeout(reconnectTimer); reconnectTimer = null }
        clearReconnectNotice()
        setConnected(true); setStatus('live'); setError(null)
      }
      next.onerror = () => {
        if (!mounted || stream !== next) return
        setConnected(false)
        setStatus('error')
        setError('Live transcript connection interrupted.')
        scheduleReconnectNotice()
        closeStream()
        scheduleReconnect()
      }
      next.onmessage = handleMessage
      // The server can use a named SSE event while keeping the same payload
      // contract. Supporting both forms keeps this client tolerant of proxy
      // and framework defaults.
      next.addEventListener('transcript', handleMessage)
      next.addEventListener('live_transcript', handleMessage)
    }

    openStream()
    return () => {
      mounted = false
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer)
      if (reconnectNoticeTimer !== null) window.clearTimeout(reconnectNoticeTimer)
      closeStream()
    }
  }, [enabled, sourceId, viewActive])

  return { items, status, error, connected, reconnecting }
}

export function HomeView({ userId, canIngest, onReview, onClip, entrySequence = 0 }: { entrySequence?: number; userId: string; canIngest: boolean; onReview: () => void; onClip: (id: string) => void }) {
  const sources = useAsync(api.sources, [], { pollMs: 3000 })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [composerIntent, setComposerIntent] = useState<SourceComposerIntent | null>(null)
  const [removedSourceIds, setRemovedSourceIds] = useState<Set<string>>(new Set())
  const [deletingSourceId, setDeletingSourceId] = useState<string | null>(null)
  const [sourceUndo, setSourceUndo] = useState<{ id: string; label: string } | null>(null)
  const [sourceActionError, setSourceActionError] = useState<string | null>(null)
  const available = (sources.data || []).filter((source) => !removedSourceIds.has(source.id))
  const selected = available.find((source) => source.id === selectedId)
  // Returning from Review keeps the selected session. The source picker can
  // still explicitly return to the welcome screen without stopping capture.
  useEffect(() => { setComposerIntent(null) }, [entrySequence])

  async function created(_message: string, source: Source) {
    sources.setData((current) => [source, ...(current || []).filter((item) => item.id !== source.id)])
    setSelectedId(source.id)
    setComposerIntent(null)
    await sources.refresh()
  }

  async function removeSource(source: Source) {
    setDeletingSourceId(source.id); setSourceActionError(null)
    try {
      await api.deleteSource(source.id)
      setRemovedSourceIds((current) => new Set(current).add(source.id))
      setSelectedId((current) => current === source.id ? null : current)
      setSourceUndo({ id: source.id, label: source.title })
      void sources.refresh()
    } catch (error) {
      setSourceActionError(error instanceof ApiRequestError ? error.message : 'The source could not be removed.')
    } finally { setDeletingSourceId(null) }
  }

  async function restoreSource() {
    if (!sourceUndo) return
    const pending = sourceUndo
    setDeletingSourceId(pending.id); setSourceActionError(null)
    try {
      await api.restoreSource(pending.id)
      setRemovedSourceIds((current) => { const next = new Set(current); next.delete(pending.id); return next })
      setSelectedId(pending.id)
      setSourceUndo(null)
      await sources.refresh()
    } catch (error) {
      setSourceActionError(error instanceof ApiRequestError ? error.message : 'The source could not be restored.')
    } finally { setDeletingSourceId(null) }
  }

  return <section className="home-page page-content" aria-label={selected ? 'Live workspace' : 'Home'}>
    <WorkspaceActions><div className="home-switcher"><Dropdown label="Switch source" value={selected?.id || ''} options={[{ value: '', label: 'Home' }, ...available.map(item => ({ value: item.id, label: item.title }))]} onChange={value => setSelectedId(value || null)} icon={<ChevronsUpDown size={18} aria-hidden="true" />} /></div>{selected && canIngest ? <DeleteButton className="home-source-delete" label={selected.title} busy={deletingSourceId === selected.id} onClick={() => void removeSource(selected)} /> : null}{canIngest ? <WorkspaceAction label="Add Source" onClick={() => setComposerIntent('any')}><Plus size={18} /></WorkspaceAction> : null}</WorkspaceActions>
    {sources.error || sourceActionError ? <div className="home-error" role="alert">{siteText(sourceActionError || sources.error)}<button onClick={() => { setSourceActionError(null); void sources.refresh() }}>Try again</button></div> : null}
    {selected ? <HomeSession key={`${userId}:${selected.id}`} source={selected} userId={userId} canIngest={canIngest} onReview={onReview} onClip={onClip} />
      : sources.loading && sources.data === null ? <WorkspaceLoading label="Loading your workspace" />
      : canIngest ? <HomeStart paused={composerIntent !== null} onUpload={() => setComposerIntent('recording')} onLive={() => setComposerIntent('live')} />
      : <div className="home-start-empty">No sources yet</div>}
    {composerIntent && canIngest ? <SourceComposer intent={composerIntent} onClose={() => setComposerIntent(null)} onCreated={created} /> : null}
    {sourceUndo ? <UndoToast message={`${sourceUndo.label} removed.`} onUndo={() => void restoreSource()} onDismiss={() => setSourceUndo(null)} busy={deletingSourceId === sourceUndo.id} /> : null}
  </section>
}

/**
 * Turn real media buffering into the startup indicator. The percentage comes
 * from HTMLMediaElement.buffered: for files it is buffered seconds over the
 * five-second startup target, and for rolling live HLS it is buffered seconds
 * over the 30 second playback-start target. Other startup prerequisites stay indeterminate so a
 * loaded video never masquerades as a fully ready workspace.
 */
function getStartupProgress(monitor: LiveMonitor | null, playerState: PlayerState, transcriptReady: boolean, media: MediaLoadProgress): LiveWorkspaceLoadingProgress {
  if (!monitor) return { value: null, label: 'Connecting to source' }
  if (!monitor.playback) return { value: null, label: 'Waiting for media' }
  if (media.value == null) {
    if (playerState === 'ready' && !transcriptReady) return { value: null, label: 'Media ready · waiting for transcript' }
    return { value: null, label: 'Buffering media' }
  }

  const detail = { bufferedSeconds: media.bufferedSeconds, targetSeconds: media.targetSeconds }
  const rollingLive = monitor.playback.kind === 'hls' && monitor.playback.live !== false
  if (media.playable) {
    if (playerState === 'ready' && transcriptReady) return { value: 1, label: 'Workspace ready', ...detail }
    if (playerState === 'ready') return { value: null, label: 'Media ready · waiting for transcript' }
    if (media.value < 1) return { value: media.value, label: rollingLive ? 'Buffering live preview' : 'Loading recording', ...detail }
    return { value: null, label: 'Media buffered · waiting to play' }
  }
  if (media.value >= 1) return { value: null, label: 'Media buffered · waiting to play', ...detail }
  return { value: media.value, label: rollingLive ? 'Buffering live preview' : 'Loading recording', ...detail }
}

function HomeSession({ source: initialSource, userId, canIngest, onReview, onClip }: { source: Source; userId: string; canIngest: boolean; onReview: () => void; onClip: (id: string) => void }) {
  const momentMarker = useMomentMark(initialSource.id, userId)
  const monitor = useAsync(() => api.liveSource(initialSource.id), [initialSource.id], { pollMs: 3000 })
  const source = monitor.data?.source || initialSource
  const capture = monitor.data?.capture || source.capture
  const captureDisconnected = isDesktopCaptureDisconnected(capture)
  const capturing = !captureDisconnected && source.kind !== 'file' && (monitor.data?.capturing ?? activeStates.has(source.status))
  const desktopCaptureActive = isDesktopCaptureActive(capture)
  // A disconnected desktop may retain a finite ENDLIST HLS preview. Keep it
  // attached as ordinary replayable media; only rolling sessions are live.
  const playback = monitor.data?.playback
  const timelineAvailable = sourceTimelineAvailable(playback)
  const captureCapabilities = useAsync(api.captureCapabilities, [], { enabled: canIngest })
  const [stopping, setStopping] = useState(false)
  const stopInFlight = useRef(false)
  const [stopRequested, setStopRequested] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [retryingDesktopCapture, setRetryingDesktopCapture] = useState(false)
  const [removedClipIds, setRemovedClipIds] = useState<Set<string>>(new Set())
  const [deletingClipId, setDeletingClipId] = useState<string | null>(null)
  const [clipUndo, setClipUndo] = useState<{ id: string; label: string } | null>(null)
  const [clipActionError, setClipActionError] = useState<string | null>(null)
  const [time, setTime] = useState(0)
  const [mobilePane, setMobilePane] = useState<'transcript' | 'clips'>('transcript')
  const video = useRef<HTMLVideoElement>(null)
  const liveTranscript = useLiveTranscript(source.id, capturing)
  const [playerState, setPlayerState] = useState<PlayerState>('waiting')
  const [previewPlaying, setPreviewPlaying] = useState(false)
  const [previewMuted, setPreviewMuted] = useState(false)
  const [playbackBlocked, setPlaybackBlocked] = useState(false)
  const [mediaLoadProgress, setMediaLoadProgress] = useState<MediaLoadProgress>({ value: null, bufferedSeconds: null, targetSeconds: null, playable: false })
  const [startupFinished, setStartupFinished] = useState(false)
  const startupFailed = Boolean(source.error || monitor.error || playerState === 'error' || source.status === 'failed')
  const preparingRecording = source.kind === 'file' && activeStates.has(source.status)
  const transcriptReady = capturing
    ? liveTranscript.connected && liveTranscript.status !== 'error'
    : Boolean(source.transcript_available || monitor.data?.transcript.segments.length || !preparingRecording && monitor.data)
  const sourceWithoutMedia = Boolean(monitor.data && !playback && !capturing && !preparingRecording)
  useEffect(() => {
    // Keep both players mounted while preparing. Live sessions need their
    // transcript channel; recordings need their first saved transcript.
    // The local picture is independent of cloud transcription. Show it as
    // soon as it plays, leaving the transcript pane to connect separately.
    if (startupFailed || sourceWithoutMedia || (monitor.data && isLiveWorkspaceReady(playerState, mediaLoadProgress.playable, transcriptReady, Boolean(playback?.local)))) setStartupFinished(true)
  }, [startupFailed, sourceWithoutMedia, monitor.data, playerState, mediaLoadProgress.playable, transcriptReady, playback?.local])
  // Keep the startup gate for normal buffering, but expose the player's
  // explicit retry when autoplay or a live stall needs user input. This does
  // not mark the media or workspace ready.
  const showStartup = !startupFinished && !startupFailed && !playbackBlocked
  const startupProgress = getStartupProgress(monitor.data, playerState, transcriptReady, mediaLoadProgress)
  const candidates = useMemo(() => (monitor.data?.candidates || []).filter((clip) => clip.status !== 'rejected' && !removedClipIds.has(clip.id)).sort((a, b) => b.created_at.localeCompare(a.created_at)), [monitor.data?.candidates, removedClipIds])
  async function stop() {
    if (stopInFlight.current || stopRequested) return
    stopInFlight.current = true
    setStopping(true); setActionError(null)
    try { await api.stopSource(source.id); setStopRequested(true); await monitor.refresh() }
    catch (error) { setActionError(error instanceof Error ? error.message : 'The stream could not be stopped.') }
    finally { stopInFlight.current = false; setStopping(false) }
  }
  async function retryCapture() {
    setRetrying(true); setActionError(null)
    try {
      await retryLatestCapture(api, source.id)
      setStartupFinished(false)
      setPlayerState('waiting')
      setPlaybackBlocked(false)
      setStopRequested(false)
      await monitor.refresh()
    } catch (error) { setActionError(error instanceof Error ? error.message : 'The stream could not be reconnected.') }
    finally { setRetrying(false) }
  }

  async function reconnectDesktopCapture() {
    setRetryingDesktopCapture(true); setActionError(null)
    try {
      await api.retryDesktopCapture(source.id)
      setStartupFinished(false)
      setPlayerState('waiting')
      setPlaybackBlocked(false)
      setStopRequested(false)
      await monitor.refresh()
    } catch (error) { setActionError(error instanceof Error ? error.message : 'This Mac could not be reconnected.') }
    finally { setRetryingDesktopCapture(false) }
  }

  async function removeClip(clip: LiveMonitor['candidates'][number]) {
    setDeletingClipId(clip.id); setClipActionError(null)
    try {
      await api.deleteClip(clip.id)
      setRemovedClipIds((current) => new Set(current).add(clip.id))
      setClipUndo({ id: clip.id, label: clip.title })
      void monitor.refresh()
    } catch (error) {
      setClipActionError(error instanceof ApiRequestError ? error.message : 'The clip could not be removed.')
    } finally { setDeletingClipId(null) }
  }

  async function restoreClip() {
    if (!clipUndo) return
    const pending = clipUndo
    setDeletingClipId(pending.id); setClipActionError(null)
    try {
      await api.restoreClip(pending.id)
      setRemovedClipIds((current) => { const next = new Set(current); next.delete(pending.id); return next })
      setClipUndo(null)
      await monitor.refresh()
    } catch (error) {
      setClipActionError(error instanceof ApiRequestError ? error.message : 'The clip could not be restored.')
    } finally { setDeletingClipId(null) }
  }
  const renameSpeaker = useCallback(async (speakerId: string, name: string) => {
    const saved = await api.renameSpeaker(initialSource.id, speakerId, name)
    monitor.setData((current) => current ? { ...current, transcript: { ...current.transcript,
      segments: current.transcript.segments.map((segment) => segment.speaker === speakerId ? { ...segment, speaker_name: saved.name || null } : segment),
    } } : current)
  }, [initialSource.id, monitor.setData])
  const seek = useCallback((seconds: number) => {
    if (seekAndPlay(video.current, seconds)) setTime(seconds)
  }, [])
  const togglePreview = useCallback(async () => {
    const element = video.current
    if (!element) return
    if (!element.paused) element.pause()
    else {
      try {
        await element.play()
        setActionError(null)
      } catch {
        setActionError('The preview could not be played. Try again.')
      }
    }
  }, [])
  return <div className={`home-workspace home-show-${mobilePane} ${showStartup ? 'is-starting' : ''} ${!playback ? 'home-workspace-waiting' : ''}`}>
    <WorkspaceActions>
      {canReconnectDesktopCapture(capture, captureCapabilities.data, userId) ? <WorkspaceAction label={retryingDesktopCapture ? 'Reconnecting…' : 'Reconnect this Mac'} disabled={retryingDesktopCapture} onClick={() => void reconnectDesktopCapture()}><RefreshCw size={17} className={retryingDesktopCapture ? 'spin' : undefined} /></WorkspaceAction> : null}
      {playback && (!showStartup || playbackBlocked) ? <WorkspaceAction label={previewPlaying ? 'Pause Preview' : 'Play Preview'} disabled={playerState === 'error' || playerState === 'empty'} onClick={() => void togglePreview()}>{previewPlaying ? <Pause size={17} /> : <Play size={17} />}</WorkspaceAction> : null}
      {playback && (!showStartup || playbackBlocked) ? <WorkspaceAction label={previewMuted ? 'Unmute Preview' : 'Mute Preview'} onClick={() => { if (video.current) { video.current.muted = !video.current.muted; setPreviewMuted(video.current.muted) } }}>{previewMuted ? <VolumeX size={17} /> : <Volume2 size={17} />}</WorkspaceAction> : null}
      {canIngest && (capturing || desktopCaptureActive) ? <WorkspaceAction label={stopping || stopRequested ? 'Stopping Stream…' : 'Stop Stream'} disabled={stopping || stopRequested} onClick={() => void stop()}><Square size={17} /></WorkspaceAction> : null}
    </WorkspaceActions>
    {showStartup ? <LiveWorkspaceLoading recording={source.kind === 'file'} progress={startupProgress} /> : null}
    <div className="home-production">
    <div className="home-broadcast">
      {actionError || clipActionError || monitor.error || (source.error && playback) ? <div className="home-error" role="alert">{siteText(actionError || clipActionError || monitor.error || source.error)}{monitor.error ? <button onClick={() => void monitor.refresh()}>Reconnect</button> : null}</div> : null}
      <div className="home-player"><LiveSourcePlayer playback={playback || null} source={source} capturing={capturing} videoRef={video} onTimeUpdate={setTime} onStateChange={setPlayerState} onPlayingChange={setPreviewPlaying} onMutedChange={setPreviewMuted} onPlaybackBlockedChange={setPlaybackBlocked} onLoadProgress={setMediaLoadProgress} onRetryCapture={canIngest && capture?.mode !== 'desktop' ? () => void retryCapture() : undefined} retryingCapture={retrying} /></div>
      {canIngest && !(showStartup && capturing) ? <SourceMoments marker={momentMarker} key={source.id} sourceId={source.id} seconds={time} live={capturing} ready={playerState === 'ready'} timelineAvailable={timelineAvailable} /> : null}
    </div>
    <div className="home-mobile-tabs" role="tablist" aria-label="Live workspace panels"><button role="tab" aria-selected={mobilePane === 'transcript'} onClick={() => setMobilePane('transcript')}>Transcript</button><button role="tab" aria-selected={mobilePane === 'clips'} onClick={() => setMobilePane('clips')}>Incoming Clips{candidates.length ? <span>{candidates.length}</span> : null}</button></div>
    <section className="home-candidates" aria-label="Incoming Clips">
      <div className="home-section-heading"><h2>Incoming Clips</h2><button onClick={onReview}>Open Review <ArrowRight size={16} /></button></div>
      <SourcePipeline sourceId={source.id} />
      <div className="home-candidate-scroll" role="region" aria-label="Incoming clip list" tabIndex={0}>
        {candidates.length ? <div className="home-candidate-grid">{candidates.map((clip) => <CandidateCard key={clip.id} clip={clip} onClick={() => onClip(clip.id)} canDelete={canIngest} deleting={deletingClipId === clip.id} onDelete={() => void removeClip(clip)} />)}</div> : null}
      </div>
    </section>
    </div>
    <TranscriptPanel onRenameSpeaker={canIngest ? renameSpeaker : undefined} segments={monitor.data?.transcript.segments || []} liveItems={liveTranscript.items} liveStatus={liveTranscript.status} liveError={liveTranscript.error} liveReconnectVisible={liveTranscript.reconnecting} capturing={capturing} loading={monitor.loading && !monitor.data} failed={source.status === 'failed'} recordingStatus={source.kind === 'file' && !source.transcript_available ? source.status : null} time={time} canSeek={Boolean(playback)} timelineAvailable={timelineAvailable} onSeek={seek} />
    {clipUndo ? <UndoToast message={`${clipUndo.label} removed.`} onUndo={() => void restoreClip()} onDismiss={() => setClipUndo(null)} busy={deletingClipId === clipUndo.id} /> : null}
  </div>
}

function CandidateCard({ clip, onClick, canDelete, deleting, onDelete }: { clip: LiveMonitor['candidates'][number]; onClick: () => void; canDelete: boolean; deleting: boolean; onDelete: () => void }) {
  const [failedImage, setFailedImage] = useState(false)
  const image = clip.version?.thumbnail_url
  useEffect(() => setFailedImage(false), [image])
  const preparing = clip.status === 'rendering'
  const duration = clip.version?.duration
  const hasDuration = typeof duration === 'number' && Number.isFinite(duration) && duration > 0
  return <div className="home-candidate">
    <button type="button" className="home-candidate-open" onClick={onClick}>
      <span className="home-candidate-image" aria-busy={preparing}>
        {image && !failedImage ? <img src={image} alt="" loading="lazy" onError={() => setFailedImage(true)} /> :
          <span className="home-candidate-placeholder">
            {preparing ? <span className="motion-logo workspace-loading-logo" aria-hidden="true" /> : <Film size={24} />}
            <span>{siteText(preparing ? 'Preparing preview' : clip.status === 'failed' ? 'Preview needs attention' : 'Open clip')}</span>
          </span>}
        {hasDuration ? <span className="home-clip-duration">{siteText(formatDuration(duration))}</span> : null}
      </span>
      <strong>{siteText(clip.title)}</strong>
      {clip.speaker ? <span className="home-candidate-speaker">{speakerCredit(clip.speaker)}</span> : null}
    </button>
    {canDelete ? <DeleteButton className="home-candidate-delete" label={clip.title} busy={deleting} onClick={(event) => { event.stopPropagation(); onDelete() }} /> : null}
  </div>
}

export function TranscriptPanel({ onRenameSpeaker, segments, liveItems, liveStatus, liveError, liveReconnectVisible, capturing, loading, failed, recordingStatus, time, canSeek, timelineAvailable = true, onSeek }: { onRenameSpeaker?: (speakerId: string, name: string) => Promise<void>; segments: TranscriptSegment[]; liveItems: LiveTranscriptItem[]; liveStatus: LiveConnectionState; liveError: string | null; liveReconnectVisible: boolean; capturing: boolean; loading: boolean; failed: boolean; recordingStatus: Source['status'] | null; time: number; canSeek: boolean; timelineAvailable?: boolean; onSeek: (seconds: number) => void }) {
  const [following, setFollowing] = useState(true)
  const scroller = useRef<HTMLDivElement>(null)
  const playbackScrollTarget = useRef<number | null>(null)
  const liveDisplay = capturing || liveStatus === 'connecting' || liveStatus === 'live' || liveStatus === 'error'
  const statusLabel = liveReconnectVisible ? 'Reconnecting…' : liveStatus === 'error' ? null : liveStatus === 'connecting' ? 'Listening…' : liveStatus === 'live' ? null : capturing ? 'Listening…' : null
  useEffect(() => {
    if (liveDisplay) setFollowing(true)
  }, [liveDisplay])
  const displaySegments = useMemo<DisplayTranscriptSegment[]>(() => {
    // The archive and realtime provider have independent clocks and ASR
    // segmentation. Combining them creates convincing-looking duplicate
    // sentences, so the live feed owns the panel for the duration of a
    // capture once it has produced its first item. The canonical transcript
    // takes over after capture stops.
    if (liveDisplay && liveItems.length) return liveItems.map((item) => ({ id: item.id, start: item.start, end: item.end, text: item.text, speaker: item.speaker, speaker_name: item.speaker_name, live: true, final: item.final })).filter((segment) => segment.text.trim())
    return segments.map((segment) => ({ ...segment })).filter((segment) => segment.text.trim())
  }, [liveDisplay, liveItems, segments])
  // Keep each diarized utterance and its word timings intact. Merging text
  // into paragraphs used to discard timings from subsequent lines.
  const paragraphs = displaySegments
  const tail = paragraphs[paragraphs.length - 1]
  useEffect(() => {
    if (liveDisplay && scroller.current) {
      scroller.current.scrollTo({ top: scroller.current.scrollHeight, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    }
  }, [liveDisplay, tail?.end, tail?.text])
  useEffect(() => {
    if (!following || !timelineAvailable || liveDisplay) { playbackScrollTarget.current = null; return }
    if (scroller.current) playbackScrollTarget.current = scrollTranscriptToActive(scroller.current, playbackScrollTarget.current)
  }, [time, following, timelineAvailable, liveDisplay, paragraphs])
  function pauseFollowing() {
    if (!liveDisplay) setFollowing(false)
  }
  const processingRecording = !paragraphs.length && recordingStatus !== null && ['queued', 'ingesting', 'transcribing'].includes(recordingStatus)
  const emptyMessage = liveReconnectVisible ? 'Transcript connection interrupted. Retrying…' : liveStatus === 'connecting' ? 'Listening for live words…' : loading ? 'Loading transcript…' : failed ? 'Transcription stopped. Check the stream error to reconnect.' : capturing ? 'Listening for the first transcribed segment…' : 'A transcript will appear here once this source has been transcribed.'
  return <section className="home-transcript" aria-label={siteText(liveDisplay ? 'Live Transcript' : 'Transcript')}>
    <div className="home-section-heading"><h2>{siteText(liveDisplay ? 'Live Transcript' : 'Transcript')}{statusLabel ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10, color: 'var(--muted)', fontSize: 11, fontWeight: 400, letterSpacing: '.01em', verticalAlign: 'middle' }}><span className="home-pulse" aria-hidden="true" />{siteText(statusLabel)}</span> : null}</h2>{displaySegments.length && !liveDisplay && timelineAvailable && !following ? <button onClick={() => { playbackScrollTarget.current = null; setFollowing(true) }}><ArrowDown size={13} />Follow Playback</button> : null}</div>
    <div ref={scroller} className="home-transcript-scroll" role="region" aria-label="Transcript text" tabIndex={0} onWheel={pauseFollowing} onTouchStart={pauseFollowing} onPointerDown={(event) => { if (event.target === event.currentTarget) pauseFollowing() }} onKeyDown={(event) => { if (event.target === event.currentTarget && isTranscriptScrollKey(event.key)) pauseFollowing() }}>
      {paragraphs.length ? paragraphs.map((segment, index) => <TranscriptLine key={segment.id || `${segment.start}-${index}`} segment={segment} onRenameSpeaker={!segment.live ? onRenameSpeaker : undefined} time={timelineAvailable ? (liveDisplay && index === paragraphs.length - 1 ? segment.start : time >= segment.start && time < segment.end ? time : -1) : -1} canSeek={canSeek && timelineAvailable && !segment.live} onSeek={onSeek} draft={Boolean(segment.live && !segment.final)} />) : processingRecording ? <div className="home-transcribing" role="status"><div className="home-transcribing-bars" aria-hidden="true"><span /><span /><span /><span /><span /></div><p>{siteText(recordingStatus === 'transcribing' ? 'Transcribing recording…' : 'Preparing recording…')}</p></div> : <div className="home-panel-empty"><AudioLines size={25} /><p>{siteText(emptyMessage)}</p>{liveReconnectVisible && liveError ? <span className="sr-only">{siteText(liveError)}</span> : null}</div>}
    </div>
  </section>
}
